import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ICON } from '@core/util/icon';
import { AddTemplateDialogComponent } from './add-template-dialog/add-template-dialog.component';
import {
	BusinessChecklistsDispatch,
	BusinessChecklistsSelectors,
} from '@state/business/checklists';
import { BusinessChecklistVM } from '@models/checklist';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { filter, map, take } from 'rxjs/operators';
import { MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	SortOptionDropdownName,
	SortOption,
} from 'src/app/shared/state/business/checklists/checklists.types';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { ActivatedRoute, Router } from '@angular/router';

export const SortDropdownOptions: DropdownOption<SortOption>[] = [
	{
		name: SortOptionDropdownName.RECENTLY_CREATED,
		value: SortOption.RECENTLY_CREATED,
	},
	{ name: SortOptionDropdownName.A_TO_Z, value: SortOption.A_TO_Z },
	{
		name: SortOptionDropdownName.DRAFT,
		value: SortOption.DRAFT,
		classes: ['italic'],
	},
	{ name: SortOptionDropdownName.FIRM_CREATED, value: SortOption.FIRM_CREATED },
	{
		name: SortOptionDropdownName.CLOSER_CREATED,
		value: SortOption.CLOSER_CREATED,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-business-checklist',
	templateUrl: './business-checklist.component.html',
	styleUrls: ['./business-checklist.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class BusinessChecklistComponent implements OnInit {
	public ICON = ICON;
	public sortDropdownOptions = SortDropdownOptions;
	public selectedSortOptionName: Observable<SortOptionDropdownName>;
	public matterFileType = MatterFileType;

	@Select(BusinessChecklistsSelectors.customTemplateExists)
	customTemplateExists$: Observable<boolean>;
	@Select(BusinessChecklistsSelectors.getSortOption)
	selectedSortOption$: Observable<SortOption>;
	@SelectSnapshot(BusinessChecklistsSelectors.getSelectedFileType)
	selectedFileType: MatterFileType | 'all';
	@Select(BusinessChecklistsSelectors.getSortedChecklists)
	sortedChecklists$: Observable<BusinessChecklistVM[]>;

	constructor(
		private matDialog: MatDialog,
		private checklistDispatch: BusinessChecklistsDispatch,
		private analytics: AnalyticsService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.selectedSortOptionName = this.selectedSortOption$.pipe(
			untilDestroyed(this),
			map((optionValue) => {
				return SortDropdownOptions.find(
					(dropdownOption) => dropdownOption.value === optionValue,
				).name as SortOptionDropdownName;
			}),
		);

		this.activatedRoute.queryParams
			.pipe(
				untilDestroyed(this),
				filter(
					(params) =>
						Object.hasOwnProperty.call(params, 'templateId') &&
						params['templateId'],
				),
				map((params) => params['templateId']),
			)
			.subscribe((templateId) => {
				this.openChecklist(templateId);
				this.removeTemplateIdParamFromUrl();
			});
	}

	addTemplate(): void {
		this.analytics.logEvent(AnalyticsEvent.CHECKLIST_ADD_TEMPLATE);
		this.matDialog.open(AddTemplateDialogComponent, {
			maxWidth: '90vw',
			data: {
				defaultFileType:
					this.selectedFileType === 'all' ? null : this.selectedFileType,
			},
		});
	}

	sortDropdownSelected(option: DropdownResult<DropdownOption<SortOption>>) {
		this.checklistDispatch.UpdateSortOption(option.result.value as SortOption);
	}

	fileTypeSelected(fileType: MatterFileType | 'all'): void {
		this.checklistDispatch.UpdateSelectedFileType(fileType);
	}

	openChecklist(selectedTemplateId: string): void {
		if (!selectedTemplateId) {
			return;
		}

		this.sortedChecklists$
			.pipe(take(1), untilDestroyed(this))
			.subscribe((sortedChecklists) => {
				this.matDialog.open(AddTemplateDialogComponent, {
					maxWidth: '90vw',
					data: {
						selectedChecklist: sortedChecklists.find(
							(checklist) => checklist.id === selectedTemplateId,
						),
						editing: false,
					},
				});
			});
	}

	removeTemplateIdParamFromUrl() {
		this.router.navigate([], {
			relativeTo: this.activatedRoute,
			queryParams: { templateId: null },
			queryParamsHandling: 'merge',
		});
	}
}
